import { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Image,
  Text,
  Separator,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import { FiX, FiShoppingCart } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ButtonC from "../../theme/ButtonC";
import SessionService from "../../services/SessionService";

import {
  updateItemQuantityServer,
  fetchCartFromServer,
  removeItemFromServer,
  updateLocalCart,
} from "../../slices/cart";
import { openLoginModal } from "../../slices/authSlice";

export default function CartPage() {
  const navigate = useNavigate();
  const { items: cartItems, 
   final_total,
    total,
    discount,
    shipping_fee,
    status: cartStatus
   } = useSelector((state) => state.cart);
  const { userLoggedIn } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [isLoggedIn, setIsLoggedIn] = useState(SessionService.isLoggedIn);
  const [loading, setLoading] = useState(false);

  const cart = cartItems || [];
  const items = cart.filter((i) => i.product && i.variant);

 
  const productTotals = {};
  items.forEach((item) => {
    const pid = item.product_id || item.product.id;
    if (!productTotals[pid]) productTotals[pid] = 0;
    productTotals[pid] += item.quantity;
  });

  const hasMOQFailure = items.some((item) => {
    const pid = item.product_id || item.product.id;
    return productTotals[pid] < item.min_order_quantity;
  });


  const updateQty = (item, quantity) => {
    setLoading(true);

    if (isLoggedIn) {
      dispatch(
        updateItemQuantityServer({
          product_id: item.product.id,
          variant_id: item.variant.id,
          quantity,
          isLoggedIn,
          product: item.product,
          variant: item.variant,
        })
      )
        .unwrap()
        .then(() => {
          dispatch(fetchCartFromServer());
        })
        .finally(() => setLoading(false));
    } else {
      dispatch(
        updateLocalCart({
          action: quantity > 0 ? "SET_QUANTITY" : "REMOVE",
          itemPayload: {
            product: item.product,
            variant: item.variant,
            quantity,
          },
        })
      );
      setLoading(false);
    }
  };


  const removeItem = (item) => {
    setLoading(true);

    if (isLoggedIn) {
      dispatch(
        removeItemFromServer({
          product_id: item.product.id,
          variant_id: item.variant.id,
        })
      )
        .unwrap()
        .then(() => dispatch(fetchCartFromServer()))
        .finally(() => setLoading(false));
    } else {
      dispatch(
        updateLocalCart({
          action: "REMOVE",
          itemPayload: {
            product: item.product,
            variant: item.variant,
          },
        })
      );
      setLoading(false);
    }
  };


  const handleCheckout = () => {
    if (!isLoggedIn) {
      dispatch(openLoginModal());
      return;
    }

    navigate("/checkout");
  };


  if (items.length === 0)
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        h="70vh"
        px={4}
        textAlign="center"
      >
        <FiShoppingCart size={50} color="gray" />
        <Text fontSize="lg" color="gray.500" mt={3}>
          Your cart is empty
        </Text>
        <ButtonC
          label="Continue Shopping"
          mt={4}
          onClick={() => navigate("/shop")}
        />
      </Flex>
    );

  const subtotal = items.reduce(
    (sum, i) => sum + i.variant.display_price * i.quantity,
    0
  );


  return (
    <Box bg="#f7f7f7" minH="100vh" py="110px" px={{ base: 3, md: 10 }}>
      <Box maxW="900px" mx="auto" bg="white" rounded="lg" boxShadow="md" p={5}>
        <Text fontSize="2xl" fontWeight="bold" mb={6}>
          Shopping Cart
        </Text>

        {loading && (
          <Flex justify="center" py={2}>
            <Spinner size="md" color="gray.600" />
          </Flex>
        )}

        <VStack align="stretch" spacing={5}>
          {items.map((item) => {
            const pid = item.product.id;
            const totalQty = productTotals[pid];
            const MOQFailed = totalQty < item.min_order_quantity;

            return (
              <Box
                key={item.id}
                borderWidth="1px"
                borderRadius="md"
                p={4}
                bg="white"
                shadow="sm"
              >
                <HStack align="start" spacing={4}>
                  <Image
                    src={item.variant?.thumb_images?.[0]?.url}
                    alt={item.product?.name}
                    boxSize={{ base: "90px", md: "110px" }}
                    objectFit="contain"
                    borderRadius="lg"
                    cursor="pointer"
                    onClick={() => navigate(`/product/${item.product.slug}`)}
                  />

            
                  <Box flex="1">
                    <HStack justify="space-between" align="start">
                      <Box>
                        <Text fontWeight="semibold" noOfLines={2}>
                          {item.product.name}
                        </Text>

                        {item.variant?.size?.name && (
                          <Text fontSize="sm" color="gray.600">
                            Size: {item.variant.size.name}
                          </Text>
                        )}

                        <Text fontSize="sm" color="gray.600">
                          ₹{item.variant.display_price}
                        </Text>
                      </Box>

                      <Box
                        as="button"
                        onClick={() => removeItem(item)}
                        bg="red.50"
                        border="1px solid #ffcccc"
                        w="34px"
                        h="34px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="md"
                        cursor="pointer"
                      >
                        <FiX size={18} color="red" />
                      </Box>
                    </HStack>

   
                    <HStack justify="space-between" mt={4}>
                      <Flex align="center" gap={3}>
                        <Box
                          as="button"
                          onClick={() => updateQty(item, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          bg="gray.100"
                          _hover={{ bg: "gray.200" }}
                          w="34px"
                          h="34px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderRadius="md"
                          border="1px solid #ddd"
                          cursor="pointer"
                          fontSize="20px"
                          color="#444"
                          _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                        >
                          –
                        </Box>

                        <Text
                          fontWeight="medium"
                          width="26px"
                          textAlign="center"
                          fontSize="16px"
                          mt="12px"
                        >
                          {item.quantity}
                        </Text>

                        <Box
                          as="button"
                          onClick={() => updateQty(item, item.quantity + 1)}
                          bg="gray.100"
                          _hover={{ bg: "gray.200" }}
                          w="34px"
                          h="34px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderRadius="md"
                          border="1px solid #ddd"
                          cursor="pointer"
                          fontSize="20px"
                          color="#444"
                        >
                          +
                        </Box>
                      </Flex>

                      <Text fontWeight="semibold">
                        ₹{(item.variant.display_price * item.quantity).toFixed(2)}
                      </Text>
                    </HStack>

                    {MOQFailed && (
                      <Text
                        fontSize="12px"
                        color="red"
                        fontStyle="italic"
                        mt={2}
                      >
                        Minimum {item.min_order_quantity} pieces required for this
                        product. You have added only {totalQty}.
                      </Text>
                    )}
                  </Box>
                </HStack>
              </Box>
            );
          })}
        </VStack>

        <Separator my={6} />

        <VStack align="stretch" spacing={3}>
          {cartItems && cartItems.length > 0 && (
                 <Box p={4} borderTopWidth="1px">
                   <HStack justify="space-between" mb={1}>
                     <Text fontWeight="medium" fontSize="sm" >Subtotal</Text>
                     <Text fontWeight="bold" fontSize="sm">
                       ₹
                       {cartItems.reduce((sum, item) => sum + (item.variant.orignial_price || item.variant.display_price) * item.quantity, 0).toFixed(2)}
                     </Text>
                   </HStack>
                   <HStack justify="space-between" mb={1}>
                     <Text fontWeight="medium" fontSize="sm">Discount</Text>
                     <Text fontWeight="bold" fontSize="sm">
                       - ₹
                       {(cartItems.reduce((sum, item) => sum + ((item.variant.orignial_price || item.variant.display_price) * item.quantity) , 0)- total).toFixed(2)}
                     </Text>
                   </HStack>
                   <HStack justify="space-between" mb={1}>
                     <Text fontWeight="medium" fontSize="sm">Shipping</Text>
                     <Text fontWeight="bold" fontSize="sm">
                       ₹
                       {shipping_fee.toFixed(2)}
                     </Text>
                   </HStack>
                   <HStack justify="space-between" mb={3} pt={2} borderTopWidth="1px">
                     <Text fontWeight="medium" fontSize="sm">Total</Text>
                     <Text fontWeight="bold" fontSize="sm">
                       ₹
                       {final_total.toFixed(2)}
                 
                     </Text>
                   </HStack>
         
                  
                 </Box>
               )}

          <ButtonC
            label="Proceed to Checkout"
            w="full"
            borderRadius="md"
            onClick={handleCheckout}
            disabled={hasMOQFailure}
          />
        </VStack>
      </Box>
    </Box>
  );
}
